import { connect } from "react-redux";
import ImageGallery from "react-image-gallery";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useState, useRef } from "react";

import "react-image-gallery/styles/css/image-gallery.css";
import { BOTTOM_NAVIGATION_VISIBILITY, TOP_NAVIGATION_VISIBILITY } from "../../constants/actionTypes";
import "./style.css";
import { imageSrcProps } from "../../utils/imageVariants";

const ImageViewComponent = (props) => {
  const device = props.device;
  const mobileHeight = props.mobileHeight ?? 240;
  const tabletHeight = props.tabletHeight ?? 300;
  const desktopHeight = props.desktopHeight ?? 500;
  const iframeHeight = device === "mobile" ? mobileHeight : device === "tablet" ? tabletHeight : desktopHeight;

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const galleryRef = useRef(null);

  const preparedImages = (props?.images || []).filter((item) => item?.imageUrl).map((item) => {
    // srcSet lets desktop / retina screens load the 1920px variant instead of upscaling the tablet copy
    const { src, srcSet, sizes } = imageSrcProps(item);
    const preparedImage = {
      original: src,
      thumbnail: item.imageUrl,
      ...(srcSet ? { srcSet, sizes } : {}),
    };

    if (props.useOriginalHeight) {
      const originalHeight = device === "mobile" ? mobileHeight : device === "tablet" ? tabletHeight : desktopHeight;
      Object.assign(preparedImage, { originalHeight: originalHeight });
    }

    return preparedImage;
  });

  const preparedYoutubeVideos = (props.linksYoutube || []).map((item) => ({
    original: item.original,
    thumbnail: item.thumbnail,
    embedUrl: item.url,
    renderItem: (item) => (
      <Box sx={{ marginBottom: 10 }}>
        <div className="video-wrapper">
          <iframe
            width="100%"
            height={iframeHeight}
            src={item.embedUrl}
            style={{ border: "none" }}
            allowFullScreen
          />
        </div>
      </Box>
    ),
  }));

  const preparedContent = [...preparedImages, ...preparedYoutubeVideos];
  const showFullScreenButton = props.fullScreenButton !== "hide" && preparedImages.length > 0;

  const openLightbox = () => {
    const currentIndex = galleryRef.current?.getCurrentIndex() ?? 0;
    setLightboxIndex(currentIndex);
    setLightboxOpen(true);
    props.setBottomNavigationVisible(false);
    props.setTopNavigationVisible(false);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    props.setBottomNavigationVisible(true);
    props.setTopNavigationVisible(true);
  };

  const navButton = (onClick, disabled, icon) => (
    <button
      type="button"
      className="image-gallery-icon"
      disabled={disabled}
      onClick={onClick}
    >
      {icon}
    </button>
  );

  return (
    <>
      <ImageGallery
        ref={galleryRef}
        items={preparedContent}
        lazyLoad={true}
        showBullets={preparedImages.length > 1}
        showThumbnails={false}
        showFullscreenButton={false}
        showPlayButton={false}
        showNav={preparedImages.length > 1}
        renderLeftNav={(onClick, disabled) => (
          <button
            type="button"
            className="image-gallery-icon image-gallery-left-nav"
            disabled={disabled}
            onClick={onClick}
            aria-label="Previous Slide"
          >
            <ChevronLeftIcon />
          </button>
        )}
        renderRightNav={(onClick, disabled) => (
          <button
            type="button"
            className="image-gallery-icon image-gallery-right-nav"
            disabled={disabled}
            onClick={onClick}
            aria-label="Next Slide"
          >
            <ChevronRightIcon />
          </button>
        )}
        renderCustomControls={() =>
          showFullScreenButton ? (
            <button
              type="button"
              className="image-gallery-icon image-gallery-fullscreen-button"
              onClick={openLightbox}
              aria-label="Open fullscreen"
            >
              <FullscreenIcon sx={{ fontSize: 20, color: "#fff" }} />
            </button>
          ) : null
        }
      />

      <Dialog
        open={lightboxOpen}
        onClose={closeLightbox}
        fullScreen
        PaperProps={{ sx: { bgcolor: "#000", m: 0 } }}
      >
        <IconButton
          onClick={closeLightbox}
          sx={{
            position: "fixed",
            top: 'calc(env(safe-area-inset-top, 0px) + 8px)',
            right: 8,
            zIndex: 10,
            color: "#fff",
            bgcolor: "rgba(0,0,0,0.45)",
            "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
            "&:focus": { outline: "none" },
          }}
        >
          <CloseIcon />
        </IconButton>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", bgcolor: "#000" }}>
          <ImageGallery
            items={preparedImages}
            startIndex={lightboxIndex}
            lazyLoad={true}
            showBullets={preparedImages.length > 1}
            showThumbnails={false}
            showFullscreenButton={false}
            showPlayButton={false}
            showNav={preparedImages.length > 1}
            renderLeftNav={(onClick, disabled) => (
              <button
                type="button"
                className="image-gallery-icon image-gallery-left-nav"
                disabled={disabled}
                onClick={onClick}
                aria-label="Previous Slide"
              >
                <ChevronLeftIcon />
              </button>
            )}
            renderRightNav={(onClick, disabled) => (
              <button
                type="button"
                className="image-gallery-icon image-gallery-right-nav"
                disabled={disabled}
                onClick={onClick}
                aria-label="Next Slide"
              >
                <ChevronRightIcon />
              </button>
            )}
          />
        </Box>
      </Dialog>
    </>
  );
};

const mapStateToProps = (state) => ({
  device: state.common.device,
});

const mapDispatchToProps = (dispatch) => ({
  setBottomNavigationVisible: (isVisible) =>
    dispatch({ type: BOTTOM_NAVIGATION_VISIBILITY, payload: isVisible }),
  setTopNavigationVisible: (isVisible) =>
    dispatch({ type: TOP_NAVIGATION_VISIBILITY, payload: isVisible }),
});

export default connect(mapStateToProps, mapDispatchToProps)(ImageViewComponent);
